import React from 'react';
import { Rocket, Eye, BrainCircuit, Clapperboard, ArrowUpRight, Zap, Target, TrendingUp } from 'lucide-react';

export const Team: React.FC = () => {
  return (
    <section id="team" className="py-24 bg-white w-full overflow-hidden">
      <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* FOUNDERS SECTION - ICON BASED */}
        <div className="mb-32 pt-10">
            {/* Header removed by request to prevent duplication */}
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {/* Edin - Sales */}
                <div className="group bg-brand-dark rounded-[2.5rem] p-10 md:p-14 relative overflow-hidden border border-gray-800 hover:border-brand-orange/50 transition-colors duration-700 ease-apple shadow-2xl animate-fade-in-up-delay-1 opacity-0">
                    <div className="absolute top-0 right-0 w-64 h-64 bg-brand-orange rounded-full blur-[100px] opacity-10 group-hover:opacity-20 transition-opacity duration-700"></div>
                    
                    <div className="relative z-10 flex flex-col h-full justify-between transform transition-transform duration-700 ease-apple group-hover:-translate-y-1">
                        <div className="w-20 h-20 bg-white/10 rounded-2xl flex items-center justify-center mb-8 border border-white/10 group-hover:scale-110 transition-transform duration-500 ease-apple">
                            <Rocket className="text-brand-orange w-10 h-10" />
                        </div>
                        
                        <div>
                            <h3 className="text-4xl font-black text-white uppercase mb-2">Edin</h3>
                            <p className="text-brand-orange font-bold uppercase tracking-widest text-sm mb-6">Sales & Ansprechpartner</p>
                            <p className="text-gray-400 font-medium leading-relaxed max-w-md">
                                "Growth starts with a handshake." Edin ist dein direkter Ansprechpartner, macht Sales und sorgt dafür, dass deine Ziele verstanden und erreicht werden.
                            </p>
                        </div>
                    </div>
                </div>

                {/* Arne - Content */}
                <div className="group bg-gray-50 rounded-[2.5rem] p-10 md:p-14 relative overflow-hidden border-2 border-gray-100 hover:border-brand-orange transition-colors duration-700 ease-apple shadow-xl animate-fade-in-up-delay-2 opacity-0">
                    <div className="relative z-10 flex flex-col h-full justify-between transform transition-transform duration-700 ease-apple group-hover:-translate-y-1">
                        <div className="w-20 h-20 bg-white rounded-2xl flex items-center justify-center mb-8 border border-gray-200 shadow-sm group-hover:scale-110 transition-transform duration-500 ease-apple">
                            <Eye className="text-brand-dark w-10 h-10" />
                        </div>
                        
                        <div>
                            <h3 className="text-4xl font-black text-brand-dark uppercase mb-2">Arne</h3>
                            <p className="text-brand-orange font-bold uppercase tracking-widest text-sm mb-6">Content Production & Planning</p>
                            <p className="text-gray-600 font-medium leading-relaxed max-w-md">
                                "Content is King, Context is God." Arne plant deinen Social-Media-Content, schreibt die Skripte und steuert die gesamte Produktion.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        {/* CORE VALUES */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-24">
            {['Speed', 'Impact', 'No BS'].map((val, i) => (
                <div key={i} className={`bg-white p-8 rounded-3xl border-2 border-gray-50 hover:border-gray-100 transition-all duration-500 ease-apple text-center shadow-lg hover:shadow-xl hover:-translate-y-1 animate-fade-in-up opacity-0`} style={{ animationDelay: `${0.4 + (i * 0.1)}s` }}>
                    <div className="w-14 h-14 bg-brand-orange/10 rounded-xl mx-auto mb-6 flex items-center justify-center text-brand-orange">
                        {i === 0 ? <Zap size={28} /> : i === 1 ? <Target size={28} /> : <TrendingUp size={28} />}
                    </div>
                    <h4 className="text-xl font-black text-brand-dark uppercase mb-2">{val}</h4>
                    <p className="text-gray-500 font-bold uppercase text-[10px] tracking-widest">
                        {i === 0 ? 'Execution at Light Speed' : i === 1 ? 'Content that Converts' : 'Results over Reports'}
                    </p>
                </div>
            ))}
        </div>

        {/* DEPARTMENTS */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
             {/* Growth Lab */}
             <div className="bg-[#111] rounded-[2.5rem] p-10 md:p-14 min-h-[400px] flex flex-col justify-between shadow-2xl relative overflow-hidden border border-gray-800 group animate-fade-in-up opacity-0" style={{ animationDelay: '0.8s' }}>
                <div className="absolute top-0 right-0 w-80 h-80 bg-brand-orange/10 rounded-full blur-[100px] transition-opacity duration-700 group-hover:opacity-40"></div>
                
                <div className="relative z-10 transform transition-transform duration-700 ease-apple group-hover:-translate-y-1">
                   <div className="flex justify-between items-start mb-12">
                       <div className="w-20 h-20 bg-white/5 rounded-2xl flex items-center justify-center backdrop-blur-sm border border-white/10 group-hover:scale-110 transition-transform duration-500 ease-apple">
                          <BrainCircuit className="text-brand-orange w-10 h-10" />
                       </div>
                       <p className="text-gray-600 font-black uppercase tracking-widest text-xs">Dept. 01</p>
                   </div>
                   
                   <h3 className="text-4xl md:text-5xl font-black text-white uppercase leading-[0.9] mb-4">Growth<br/>Lab</h3>
                   <p className="text-gray-400 text-lg font-medium leading-relaxed max-w-sm">
                      Datengetriebene Vertriebsstrategien. Wir analysieren den Markt und bauen Funnels, die Aufmerksamkeit in zahlende Kunden verwandeln.
                   </p>
                </div>
             </div>

             {/* Creative Studio */}
             <div className="bg-white border-2 border-gray-100 rounded-[2.5rem] p-10 md:p-14 min-h-[400px] flex flex-col justify-between shadow-xl relative overflow-hidden group animate-fade-in-up opacity-0" style={{ animationDelay: '1.0s' }}>
                
                <div className="relative z-10 transform transition-transform duration-700 ease-apple group-hover:-translate-y-1">
                   <div className="flex justify-between items-start mb-12">
                       <div className="w-20 h-20 bg-gray-50 rounded-2xl flex items-center justify-center border border-gray-100 group-hover:scale-110 transition-transform duration-500 ease-apple">
                          <Clapperboard className="text-brand-dark w-10 h-10" />
                       </div>
                       <p className="text-gray-300 font-black uppercase tracking-widest text-xs">Dept. 02</p>
                   </div>
                   
                   <h3 className="text-4xl md:text-5xl font-black text-brand-dark uppercase leading-[0.9] mb-4">Creative<br/>Studio</h3>
                   <p className="text-gray-600 text-lg font-medium leading-relaxed max-w-sm">
                      High-End Content Produktion. Vom ersten Skript bis zum fertigen Reel – wir produzieren Assets, die deine Brand zur lokalen Authority machen.
                   </p>
                </div>
                <div className="absolute bottom-10 right-10 opacity-20 group-hover:opacity-40 group-hover:translate-x-2 group-hover:-translate-y-2 transition-all duration-700 ease-apple">
                    <ArrowUpRight className="text-brand-orange w-12 h-12" />
                </div>
             </div>
        </div>
      </div>
    </section>
  );
};